export const heroBenefits = [
  "A timed 30-minute plan with the words to use",
  "Realistic role-play cases with seller, buyer and observer cards",
  "Observation sheets that keep feedback on behavior",
];

export const stats = [
  { value: "10", label: "Workshops" },
  { value: "30", label: "Minutes per session" },
  { value: "5", label: "Included bonuses" },
  { value: "$27", label: "USD, one time" },
];

export const situations = [
  {
    id: "price",
    label: "Sellers defend or discount as soon as price comes up",
    workshop: "Workshop 5 · Price conversations",
    title: "Clarify the price concern before responding",
    description:
      "Sellers practice asking what \"too expensive\" is being compared with — a budget, another quote or an expectation — before they answer.",
    outcome:
      "Each seller asks one clarifying question, then compares scope using only what they actually know.",
    pairWith: "Pair it with Workshop 6 · Comparing alternatives",
  },
  {
    id: "discovery",
    label: "Discovery stays shallow before the pitch",
    workshop: "Workshop 2 · Discovery questions",
    title: "Uncover needs before pitching",
    description:
      "Sellers practice open questions about the buyer's situation and priorities, and hold the presentation until they understand what matters.",
    outcome:
      "Sellers can name the buyer's main priority in the buyer's own words before proposing anything.",
    pairWith: "Then run Workshop 3 · Identifying fit",
  },
  {
    id: "features",
    label: "Sellers list every feature",
    workshop: "Workshop 4 · Explaining value",
    title: "Connect the proposal to what the buyer said",
    description:
      "Sellers practice linking each part of the proposal to a need the buyer stated, instead of walking through everything the service does.",
    outcome:
      "Each point in the proposal is tied to something the buyer said earlier in the conversation.",
    pairWith: "Builds on Workshop 2 · Discovery questions",
  },
  {
    id: "next-steps",
    label: "Conversations stall without a next step",
    workshop: "Workshop 7 · \"Send me some information\"",
    title: "Agree on useful information and a next step",
    description:
      "Sellers practice turning \"send me something\" into a clear agreement: what would help, who it's for and when you'll talk next.",
    outcome:
      "Conversations end with a specific, agreed next step instead of an open-ended promise to follow up.",
    pairWith: "Follow with Workshop 9 · Useful follow-up",
  },
];

export const samplePages = [
  {
    id: "plan",
    page: "PAGE 1",
    label: "Facilitator plan",
    heading: "A timed plan with the words to use",
    text: "The 30-minute agenda, the goal for the session and the opening line — so you know what to say and when.",
  },
  {
    id: "case",
    page: "PAGE 2",
    label: "Case A brief",
    heading: "A complete fictional case, scope and price included",
    text: "ClearDesk Support's offer and Jordan's situation, written out so nobody has to invent a scenario on the spot.",
  },
  {
    id: "roles",
    page: "PAGE 3",
    label: "Seller & buyer cards",
    heading: "Role cards that make practice realistic",
    text: "The seller gets an opening question and what to avoid. The buyer gets an opening line and information to reveal only if asked.",
  },
  {
    id: "observer",
    page: "PAGE 4",
    label: "Observation sheet",
    heading: "Feedback on behavior, not personality",
    text: "Three observable behaviors to check during practice. It's a practice checklist, not an employee rating.",
  },
] as const;

export type SamplePageId = (typeof samplePages)[number]["id"];

export const solutionSteps = [
  {
    title: "Choose",
    body: "Pick the workshop that matches what you hear in your team's conversations. The Needs & Skill Check Kit helps you decide.",
  },
  {
    title: "Prepare",
    body: "Read the facilitator plan and print the participant pages. Cases, role cards and model responses are already written.",
  },
  {
    title: "Practice",
    body: "Run the 30 minutes: 3 opening, 5 demonstration, 15 role rotations, 5 debrief and 2 application.",
  },
  {
    title: "Apply",
    body: "Each seller leaves with one action for real conversations, plus follow-through prompts for the week.",
  },
];

export const includedItems = [
  {
    title: "Two manual volumes",
    body: "Volume 1 covers Workshops 1–5, Volume 2 covers Workshops 6–10. Both are part of the $27 core kit.",
  },
  {
    title: "30-minute facilitator plans",
    body: "A timed agenda for every workshop, with facilitator wording for each segment.",
  },
  {
    title: "Two fictional cases per workshop",
    body: "Case A runs in the timed session. Case B is for an alternative or a later repeat.",
  },
  {
    title: "Seller, buyer and observer cards",
    body: "Printable role cards so every participant knows what to do in each rotation.",
  },
  {
    title: "Model responses",
    body: "Example wording to demonstrate the skill and discuss in the debrief.",
  },
  {
    title: "Observation sheets and follow-through",
    body: "A behavioral feedback checklist and one application action per workshop.",
  },
];

export const offerIncludes = [
  "Two manual volumes: Workshops 1–5 and Workshops 6–10",
  "Ten 30-minute facilitator plans with timed agendas and wording",
  "Two fictional cases per workshop — Case A for the session, Case B for a repeat",
  "Seller, buyer and observer role cards",
  "Model responses and behavioral observation sheets",
  "A follow-through application action for every workshop",
];
